/**
 * SummaryPanel - Panel resumen colapsable P4 Exportar
 * 
 * Panel que muestra comparativa Antes/Después con:
 * - Header con métricas principales (cobertura, score, errores)
 * - Flecha central de transformación
 * - Toggle expandir/colapsar
 * - Contenido expandido con detalles, distribución scores y tipologías
 * 
 * Extraído de: P3_Descarga_v8_final.html (.panel, .panel-header, .header-row)
 * 
 * @version 1.0.0
 * @date 2026-01-12
 */

import React, { useState, useRef, useEffect } from 'react';
import { SummaryPanelContent } from './SummaryPanelContent';
import type { SummaryPanelContentProps } from './SummaryPanelContent';
import { colors, typography, borderRadius, transitions } from '@/design-system/v2';

const GAP_LG = '12px';
const GAP_SM = '6px';

// ============================================================================
// TIPOS
// ============================================================================

export interface SummaryStats {
  // Antes
  coverageBefore: number;      // % cobertura
  avgScoreBefore: number;      // score promedio
  erroresBefore: number;       // errores UTF-8
  infrasBefore: number;        // total infraestructuras
  conCoordsBefore: number;     // con coordenadas
  coordsTruncadasBefore: number;
  // Después
  coverageAfter: number;
  avgScoreAfter: number;
  erroresAfter: number;
  infrasAfter: number;
  conCoordsAfter: number;
  coordsTruncadasAfter: number;
}

export interface SummaryPanelProps {
  /** Estadísticas comparativas */
  stats: SummaryStats;
  /** Datos del contenido expandido (DetailGrid + ScoreBars + TypologyGrid) */
  contentProps?: SummaryPanelContentProps;
  /** Estado inicial expandido */
  defaultOpen?: boolean;
}

// ============================================================================
// ICONOS SVG
// ============================================================================

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v6l4 2"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ============================================================================
// ESTILOS
// ============================================================================

const STYLES = {
  panel: {
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    marginBottom: '24px',
  },
  panelHeader: {
    padding: '16px 20px', // alineado con padding 20px de los otros paneles
    cursor: 'pointer',
    transition: transitions.base,
  },
  panelHeaderHover: {
    background: colors.bg.tertiary,
  },
  panelTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  },
  panelTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: GAP_LG,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  panelTitleIcon: {
    width: '16px',
    height: '16px',
    color: colors.primary,
  },
  // Header Row: mismas proporciones que el colgroup de la tabla (30% / 40% / 30%)
  // El toggle está en panelTitleRow, NO aquí
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '30% 40% 30%',
    alignItems: 'center',
    width: '100%',
  },
  headerHalf: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  // ANTES: alineado a la derecha del 30% — alignItems (eje horizontal en flex-column)
  headerHalfBefore: {
    alignItems: 'flex-end',
  },
  // DESPUÉS: alineado a la izquierda del 30% — alignItems (eje horizontal en flex-column)
  headerHalfAfter: {
    alignItems: 'flex-start',
  },
  headerLabel: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: GAP_SM,
    whiteSpace: 'nowrap' as const,
  },
  headerLabelBefore: {
    color: colors.fg.muted,
  },
  headerLabelAfter: {
    color: colors.primary,
  },
  headerLabelIcon: {
    width: '14px',
    height: '14px',
  },
  headerMetrics: {
    display: 'flex',
    gap: '24px',
  },
  headerMetric: {
    textAlign: 'center' as const,
  },
  headerMetricValue: {
    fontFamily: typography.fontFamily.mono,
    fontSize: '20px',
    fontWeight: typography.fontWeight.bold,
    lineHeight: 1.1,
  },
  headerMetricValueMuted: {
    color: colors.fg.secondary,
  },
  headerMetricValueSuccess: {
    color: colors.primary,
  },
  headerMetricLabel: {
    fontSize: typography.fontSize['2xs'],
    color: colors.fg.muted,
    marginTop: '3px',
  },
  headerArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    background: 'rgba(45, 212, 191, 0.12)',
    borderRadius: '50%',
    flexShrink: 0,
    margin: '0 auto', // centra el círculo dentro de la celda del 40%
  },
  headerArrowIcon: {
    width: '18px',
    height: '18px',
    stroke: colors.primary,
  },
  headerToggle: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.bg.tertiary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: '8px',
    marginLeft: '12px',
    flexShrink: 0,
  },
  headerToggleIcon: {
    width: '14px',
    height: '14px',
    stroke: colors.fg.secondary,
    transition: 'transform 0.2s',
    strokeWidth: '2.5',
  },
  headerToggleIconOpen: {
    transform: 'rotate(180deg)',
  },
  // Panel Body (contenido expandido)
  // maxHeight se calcula dinámicamente con ResizeObserver — ver componente
  panelBody: {
    maxHeight: '0',
    overflow: 'hidden',
    transition: 'max-height 0.4s ease',
  },
  panelContent: {
    padding: '20px',
    borderTop: `1px solid ${colors.border.DEFAULT}`,
  },
} as const;

// ============================================================================
// COMPONENTE
// ============================================================================

// Inyección de keyframes pulse una sola vez
// ID versionado: fuerza re-inyección tras cambios en dev
const PULSE_STYLE_ID = 'ptel-chevron-pulse-v4';
if (typeof document !== 'undefined') {
  // Limpiar versiones anteriores
  ['ptel-chevron-pulse','ptel-chevron-pulse-v2','ptel-chevron-pulse-v3'].forEach(id => {
    document.getElementById(id)?.remove();
  });
  if (!document.getElementById(PULSE_STYLE_ID)) {
    const s = document.createElement('style');
    s.id = PULSE_STYLE_ID;
    // Pulse continuo suave: ondulación teal cada 4s con pausa larga
    s.textContent = `
      @keyframes ptel-pulse {
        0%,  100% { border-color: var(--teal); }
        50%        { border-color: rgba(45, 212, 191, 0.4); }
      }
      .ptel-toggle-pulse {
        animation: ptel-pulse 2.5s ease-in-out infinite;
      }
    `;
    document.head.appendChild(s);
  }
}

export const SummaryPanel: React.FC<SummaryPanelProps> = ({
  stats,
  contentProps,
  defaultOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // pulseKey: cambia cada vez que se cierra el panel → reinicia la animación
  const [pulseKey, setPulseKey] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Altura real del contenido expandido — evita el truncado con maxHeight fijo
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setContentHeight(el.scrollHeight);
    });
    ro.observe(el);
    // Medida inicial
    setContentHeight(el.scrollHeight);
    return () => ro.disconnect();
  }, []);

  const togglePanel = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (!next) setPulseKey(k => k + 1); // cerrado → reinicia pulse
  };

  return (
    <div style={STYLES.panel}>
      {/* Header (siempre visible) */}
      <div
        style={{
          ...STYLES.panelHeader,
          ...(isHovered ? STYLES.panelHeaderHover : {}),
        }}
        onClick={togglePanel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Título + toggle en la misma fila */}
        <div style={STYLES.panelTitleRow}>
          <div style={STYLES.panelTitle}>
            <span style={STYLES.panelTitleIcon}><ChartIcon /></span>
            Resumen del proceso
          </div>
          {/* Toggle aquí — fuera del grid de métricas para no desalinear */}
          <div
            key={isOpen ? 'open' : `closed-${pulseKey}`}
            style={STYLES.headerToggle}
            className={isOpen ? '' : 'ptel-toggle-pulse'}
          >
            <span style={{
              ...STYLES.headerToggleIcon,
              ...(isOpen ? STYLES.headerToggleIconOpen : {}),
            }}>
              <ChevronDownIcon />
            </span>
          </div>
        </div>

        {/* Métricas Antes → Después */}
        <div style={STYLES.headerRow}>
          {/* ANTES: flex-column, contenido pegado al borde derecho del 30% */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
            <div style={{ ...STYLES.headerLabel, ...STYLES.headerLabelBefore }}>
              <span style={STYLES.headerLabelIcon}><ClockIcon /></span>
              Antes
            </div>
            <div style={STYLES.headerMetrics}>
              <div style={STYLES.headerMetric}>
                <div style={{ ...STYLES.headerMetricValue, ...STYLES.headerMetricValueMuted }}>{stats.coverageBefore}%</div>
                <div style={STYLES.headerMetricLabel}>geolocalización</div>
              </div>
              <div style={STYLES.headerMetric}>
                <div style={{ ...STYLES.headerMetricValue, ...STYLES.headerMetricValueMuted }}>{stats.avgScoreBefore}</div>
                <div style={STYLES.headerMetricLabel}>score</div>
              </div>
              <div style={STYLES.headerMetric}>
                <div style={{ ...STYLES.headerMetricValue, ...STYLES.headerMetricValueMuted }}>{stats.erroresBefore}</div>
                <div style={STYLES.headerMetricLabel}>errores</div>
              </div>
            </div>
          </div>

          {/* Flecha central */}
          <div style={STYLES.headerArrow}>
            <span style={STYLES.headerArrowIcon}><ArrowRightIcon /></span>
          </div>

          {/* DESPUÉS: flex-column, contenido pegado al borde izquierdo del 30% */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ ...STYLES.headerLabel, ...STYLES.headerLabelAfter }}>
              Después
              <span style={STYLES.headerLabelIcon}><CheckCircleIcon /></span>
            </div>
            <div style={STYLES.headerMetrics}>
              <div style={STYLES.headerMetric}>
                <div style={{ ...STYLES.headerMetricValue, ...STYLES.headerMetricValueSuccess }}>{stats.coverageAfter}%</div>
                <div style={STYLES.headerMetricLabel}>geolocalización</div>
              </div>
              <div style={STYLES.headerMetric}>
                <div style={{ ...STYLES.headerMetricValue, ...STYLES.headerMetricValueSuccess }}>{stats.avgScoreAfter}</div>
                <div style={STYLES.headerMetricLabel}>score</div>
              </div>
              <div style={STYLES.headerMetric}>
                <div style={{ ...STYLES.headerMetricValue, ...STYLES.headerMetricValueSuccess }}>{stats.erroresAfter}</div>
                <div style={STYLES.headerMetricLabel}>errores</div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Body (contenido expandido) */}
      <div style={{
        ...STYLES.panelBody,
        maxHeight: isOpen ? `${contentHeight}px` : '0',
      }}>
        <div ref={contentRef} style={STYLES.panelContent} data-testid="summary-panel-content">
          {contentProps ? (
            <SummaryPanelContent {...contentProps} />
          ) : (
            <div style={{
              textAlign: 'center',
              color: colors.fg.muted,
              padding: '40px',
            }}>
              Sin datos detallados disponibles
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryPanel;
